import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Box, Divider, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import AccessibilityOptions from './AccessibilityOptions';
import routes from '../data/Navigation';
import { tokens } from '../theme';

// Footer columns, following the Stitch footer's four-column grid. Pages not listed here
// fall into "Work", so a new footer page always appears somewhere.
const columns: { title: string; paths: string[] }[] = [
  { title: 'Company', paths: ['/', '/about-us', '/services', '/contact-us'] },
  { title: 'Work', paths: [] },
  { title: 'Help & trust', paths: ['/faq', '/privacy'] },
];

const listed = routes.filter((route) => route.footer && route.listInFooter !== false);
const assigned = new Set(columns.flatMap((column) => column.paths));
const linksFor = (column: (typeof columns)[number]) =>
  column.title === 'Work'
    ? listed.filter((route) => !assigned.has(route.path))
    : column.paths.flatMap((path) => listed.filter((route) => route.path === path));

const linkSx = {
  color: tokens.onNavyMuted,
  textDecoration: 'none',
  '&:hover': { color: '#FFFFFF', textDecoration: 'underline' },
};

export default function Footer() {
  return (
    <Box
      component='footer'
      sx={{ backgroundColor: tokens.navy, color: '#FFFFFF', mt: { xs: 8, md: 12 } }}
    >
      <Box
        sx={{
          // Same column as the page Container (1200px including 24px gutters).
          boxSizing: 'border-box',
          maxWidth: 1200,
          mx: 'auto',
          px: 3,
          pt: 5,
          pb: 3,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '4fr 3fr 3fr 2fr' },
            gap: 4,
            pb: 5,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component='img'
                src='/brand/mark-reversed.svg'
                alt=''
                aria-hidden='true'
                sx={{ height: 32, width: 32 }}
              />
              <Typography
                variant='h5'
                component='h2'
                sx={{ color: '#FFFFFF', fontWeight: 700 }}
              >
                Scaled Tech Consulting
              </Typography>
            </Box>
            <Typography
              variant='bodySm'
              sx={{ color: tokens.onNavyMuted, maxWidth: '24rem' }}
            >
              Custom applications, content platforms, and connected workflows. Based in the United
              States, working remotely with teams around the world.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MailOutlineIcon
                aria-hidden='true'
                sx={{ fontSize: 18, color: tokens.cyan }}
              />
              <Typography
                component='a'
                variant='labelMd'
                href='mailto:info@scaledtech.consulting'
                sx={{ ...linkSx, color: '#FFFFFF', overflowWrap: 'anywhere' }}
              >
                info@scaledtech.consulting
              </Typography>
            </Box>
            <Typography
              variant='bodySm'
              sx={{ color: tokens.onNavyMuted }}
            >
              Report an accessibility barrier:{' '}
              <Box
                component='a'
                href='mailto:info@scaledtech.consulting?subject=Website%20accessibility'
                aria-label='Email info@scaledtech.consulting about accessibility'
                // Inside a sentence, so it needs more than colour to read as a link.
                sx={{
                  ...linkSx,
                  color: '#FFFFFF',
                  textDecoration: 'underline',
                  overflowWrap: 'anywhere',
                }}
              >
                info@scaledtech.consulting
              </Box>
            </Typography>
            <AccessibilityOptions inverted />
          </Box>
          {columns.map((column) => (
            <Box
              key={column.title}
              component='nav'
              aria-label={`Footer: ${column.title}`}
            >
              <Typography
                variant='labelCaps'
                component='h3'
                sx={{ color: tokens.cyan, display: 'block', mb: 1.5 }}
              >
                {column.title}
              </Typography>
              <Box
                component='ul'
                sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}
              >
                {linksFor(column).map((route) => (
                  <li key={route.path}>
                    <Typography
                      component={Link}
                      to={route.path}
                      variant='bodySm'
                      // Padding gives the focus ring room; the negative margin keeps the text
                      // aligned with the column heading.
                      sx={{ ...linkSx, ml: '-6px' }}
                    >
                      {route.label1} {route.label2}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
        <Typography
          variant='bodySm'
          sx={{ color: tokens.onNavyMuted, display: 'block', pt: 3 }}
          // The build year is in the prerendered HTML; a visit in a later year renders the new one.
          suppressHydrationWarning
        >
          © {new Date().getFullYear()} Scaled Tech Consulting. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}
